import {
  cloneElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn, usePrefersReducedMotion } from '@/lib/utils';
import { Portal } from './Portal';
import './Menu.css';

/**
 * Menu — o painel de ações e escolhas do console.
 *
 * Um só vocabulário para os dois usos que a Onda H pediu: o "⋯" de uma linha de
 * tabela e o botão único do compositor de perguntas. Composicional em vez de
 * orientado a dados porque as duas superfícies desenham a MESMA linha com
 * conteúdos diferentes (um radio com check, uma linha com submenu, um item
 * destrutivo), e uma prop `items` acabaria virando um dialeto de configuração
 * para cada uma.
 *
 * ```tsx
 * <Menu label="Mais ações de Direito" trigger={<IconButton label="⋯" icon={<MoreHorizontal />} />}>
 *   <MenuItem onSelect={rename}>Renomear</MenuItem>
 *   <MenuDivider />
 *   <MenuItem danger onSelect={remove}>Excluir área</MenuItem>
 * </Menu>
 * ```
 *
 * Peças: `MenuGroup` (cabeçalho pintado, não focável), `MenuItem`,
 * `MenuRadioItem` (escolha única, `Check` à direita), `MenuSub` +
 * `MenuSubContent` (submenu lateral ou drill-down), `MenuDivider`,
 * `MenuScrollArea` (bloco com teto e rolagem própria, para listas longas).
 *
 * Três decisões que o código sozinho não explica:
 *
 * 1. **Portal com `position: fixed`.** O gatilho vive dentro de painéis com
 *    `overflow` (o compositor, a tabela da Taxonomia): um painel `absolute`
 *    seria recortado. Fixo e reposicionado em `scroll`/`resize` é o preço para
 *    não depender de anchor positioning, que ainda não existe em Firefox/Safari.
 *
 * 2. **Foco REAL nos itens, sem `aria-activedescendant`.** Com foco de verdade o
 *    `:focus-visible` do sistema desenha o anel sozinho e o leitor de tela lê a
 *    linha sem precisar que o painel espelhe estado. O preço é o roving
 *    tabindex ser aplicado no DOM (`tabIndex` mutado ao mover), o que é seguro
 *    aqui porque `Tab` FECHA o menu: a única parada de tabulação que importa
 *    dura o tempo de uma tecla.
 *
 * 3. **A lista de itens é lida do DOM, não de um registro.** Grupos, áreas de
 *    rolagem e submenus aninham a árvore; a ordem que as setas precisam seguir é
 *    exatamente a ordem do documento, e qualquer registro em contexto seria uma
 *    segunda opinião sobre ela, com chance de divergir.
 *
 * O submenu abre ao lado enquanto couber na área de leitura (o `main` do
 * console, nunca a viewport: a viewport inclui o menu lateral, que pinta por
 * cima). Abaixo de 720px, ou sem espaço lateral nenhum, ele vira DRILL-DOWN:
 * substitui o painel e ganha uma primeira linha "Voltar".
 */

export type MenuPlacement = 'auto' | 'up' | 'down';
export type MenuAlign = 'auto' | 'start' | 'end';

/** Folga entre gatilho e painel, e entre painel e borda da área de leitura. */
const GAP = 6;
/** `min-width` de `.menu__panel--sub`: a largura mínima que um submenu ocupa. */
const SUB_MIN_WIDTH = 200;
/** Abaixo disto não há espaço para dois painéis lado a lado. */
const DRILL_BREAKPOINT = 720;
/** Hover só abre submenu depois desta pausa: passar o mouse não é escolher. */
const HOVER_INTENT_MS = 150;
/** Silêncio que zera a busca por prefixo. */
const TYPEAHEAD_RESET_MS = 500;
/** Teclas que acompanham uma letra em vez de substituí-la. */
const MODIFIER_KEYS = new Set(['Shift', 'Control', 'Alt', 'AltGraph', 'Meta', 'CapsLock']);

const ITEM_SELECTOR = '[data-menu-item]:not([aria-disabled="true"])';

interface MenuContextValue {
  ownerId: string;
  close: () => void;
  openSubId: string | null;
  drillId: string | null;
  drillHost: HTMLElement | null;
  /** Abre um submenu no modo que o próprio `MenuSub` mediu. */
  requestSub: (subId: string, label: string, mode: 'side' | 'drill') => void;
  /** Fecha o submenu e devolve o foco à linha-mãe. */
  closeSub: (subId: string) => void;
  /** Fecha submenu aberto por hover, sem mexer no foco. */
  leaveSubs: () => void;
  /** A área de leitura medida a partir do GATILHO, que é quem ainda vive nela. */
  areaRect: () => DOMRect;
}

const MenuContext = createContext<MenuContextValue | null>(null);

function useMenuContext(component: string): MenuContextValue {
  const ctx = useContext(MenuContext);
  if (!ctx) throw new Error(`<${component}> precisa estar dentro de <Menu>.`);
  return ctx;
}

/**
 * A área em que um painel pode abrir sem sair da leitura: o `main` do console,
 * ou a viewport quando não há um (o widget do site, os testes). Deliberadamente
 * NÃO a viewport no primeiro caso — ela inclui o menu lateral.
 */
function readingRect(el: HTMLElement | null): DOMRect {
  const host = el?.closest('main') ?? document.documentElement;
  const rect = host.getBoundingClientRect();
  // Documento sem layout (jsdom) mede tudo em zero: aí a viewport é a única
  // informação honesta que resta.
  if (rect.width > 0) return rect;
  return new DOMRect(0, 0, window.innerWidth, window.innerHeight);
}

interface Coords {
  top: number;
  left: number;
}

interface PanelPlacement extends Coords {
  direction: 'up' | 'down';
}

function placePanel(
  trigger: HTMLElement,
  panel: HTMLElement,
  area: DOMRect,
  placement: MenuPlacement,
  align: MenuAlign,
): PanelPlacement {
  const t = trigger.getBoundingClientRect();
  const p = panel.getBoundingClientRect();
  const viewportH = window.innerHeight || area.height;

  const spaceBelow = viewportH - t.bottom;
  const direction: 'up' | 'down' =
    placement === 'up' || (placement === 'auto' && spaceBelow < p.height + GAP && t.top > spaceBelow)
      ? 'up'
      : 'down';

  const top = clamp(
    direction === 'up' ? t.top - GAP - p.height : t.bottom + GAP,
    GAP,
    viewportH - p.height - GAP,
  );

  let left: number;
  if (align === 'end') left = t.right - p.width;
  else if (align === 'start') left = t.left;
  // 'auto': cresce para a direita enquanto couber, senão ancora à direita do
  // gatilho e cresce para a esquerda.
  else left = t.left + p.width > area.right ? t.right - p.width : t.left;

  return { top, left: clamp(left, area.left + GAP, area.right - p.width - GAP), direction };
}

function placeSubPanel(row: HTMLElement, panel: HTMLElement, area: DOMRect): Coords {
  const r = row.getBoundingClientRect();
  const p = panel.getBoundingClientRect();
  const viewportH = window.innerHeight || area.height;
  const flipsLeft = area.right - r.right < p.width && r.left - area.left >= p.width;
  return {
    // Alinhado ao TOPO da linha, descontado o padding do painel: a primeira
    // opção do submenu nasce na mesma altura da linha que a abriu.
    top: clamp(r.top - 4, GAP, viewportH - p.height - GAP),
    left: clamp(flipsLeft ? r.left - p.width : r.right, area.left, area.right - p.width),
  };
}

/** Lateral enquanto houver espaço de um lado ou do outro; drill-down abaixo disso. */
function subMode(row: HTMLElement, area: DOMRect): 'side' | 'drill' {
  if (window.innerWidth < DRILL_BREAKPOINT) return 'drill';
  const r = row.getBoundingClientRect();
  const fits = area.right - r.right >= SUB_MIN_WIDTH || r.left - area.left >= SUB_MIN_WIDTH;
  return fits ? 'side' : 'drill';
}

function clamp(value: number, min: number, max: number): number {
  // Painel mais alto que a área: o mínimo ganha, porque cortar embaixo mantém o
  // começo da lista legível e cortar em cima esconde o primeiro item.
  return Math.max(min, Math.min(value, Math.max(min, max)));
}

/** Itens focáveis de um painel, na ordem do documento. Desabilitados ficam de fora. */
function itemsOf(pane: HTMLElement): HTMLElement[] {
  return Array.from(pane.querySelectorAll<HTMLElement>(ITEM_SELECTOR));
}

/** Move o foco e o único `tabIndex=0` do painel para `el`. */
function focusItem(el: HTMLElement): void {
  const pane = el.closest<HTMLElement>('[data-menu-pane]');
  if (pane) {
    for (const other of itemsOf(pane)) other.tabIndex = other === el ? 0 : -1;
  }
  el.focus();
  // jsdom não implementa o método; a chamada opcional é por causa dele.
  el.scrollIntoView?.({ block: 'nearest' });
}

/** Texto que o typeahead compara: o rótulo da linha, nunca a descrição. */
function itemText(el: HTMLElement): string {
  const label = el.querySelector('.menu__item-label') ?? el;
  return normalize(label.textContent ?? '');
}

/** Primeiro e último ponto de código do bloco "Combining Diacritical Marks". */
const COMBINING_FIRST = 0x300;
const COMBINING_LAST = 0x36f;

/** Sem acentos e em caixa baixa: quem digita "area" precisa achar "Área". */
function normalize(text: string): string {
  let out = '';
  for (const char of text.normalize('NFD')) {
    const code = char.codePointAt(0) ?? 0;
    if (code >= COMBINING_FIRST && code <= COMBINING_LAST) continue;
    out += char;
  }
  return out.trim().toLowerCase();
}

export interface MenuProps {
  /**
   * O gatilho, escrito por quem usa. O Menu o clona para pendurar a ref,
   * `aria-haspopup="menu"`, `aria-expanded` e a abertura por clique/setas —
   * preservando os handlers que já estavam nele.
   */
  trigger: ReactElement;
  /** Nome acessível do painel. */
  label: string;
  placement?: MenuPlacement;
  align?: MenuAlign;
  onOpenChange?: (open: boolean) => void;
  /** Classe extra no painel, para largura própria da superfície que usa. */
  className?: string;
  children: ReactNode;
}

export function Menu({
  trigger,
  label,
  placement = 'auto',
  align = 'auto',
  onOpenChange,
  className,
  children,
}: MenuProps): JSX.Element {
  const ownerId = useId().replace(/:/g, '');
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<Coords | null>(null);
  const [direction, setDirection] = useState<'up' | 'down'>('down');
  const [panelEl, setPanelEl] = useState<HTMLDivElement | null>(null);
  const [openSubId, setOpenSubId] = useState<string | null>(null);
  const [drill, setDrill] = useState<{ id: string; label: string } | null>(null);
  const [drillHost, setDrillHost] = useState<HTMLElement | null>(null);

  const triggerRef = useRef<HTMLElement | null>(null);
  // Espelho de `drill` para os callbacks estáveis, que não podem depender dele
  // sem se recriarem a cada abertura de submenu.
  const drillRef = useRef(drill);
  drillRef.current = drill;
  const openEdge = useRef<'first' | 'last'>('first');
  const focusedOnOpen = useRef(false);
  const rowToRefocus = useRef<string | null>(null);
  const typeahead = useRef({ query: '', at: 0 });
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;

  const reduceMotion = usePrefersReducedMotion();

  const areaRect = useCallback(() => readingRect(triggerRef.current), []);

  const openMenu = useCallback((edge: 'first' | 'last') => {
    openEdge.current = edge;
    focusedOnOpen.current = false;
    setCoords(null);
    setOpen(true);
    onOpenChangeRef.current?.(true);
  }, []);

  const close = useCallback((restoreFocus = true) => {
    setOpen(false);
    setOpenSubId(null);
    setDrill(null);
    setDrillHost(null);
    setCoords(null);
    onOpenChangeRef.current?.(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  const requestSub = useCallback(
    (subId: string, subLabel: string, mode: 'side' | 'drill'): void => {
      setOpenSubId(subId);
      setDrill(mode === 'drill' ? { id: subId, label: subLabel } : null);
    },
    [],
  );

  const closeSub = useCallback(
    (subId: string): void => {
      setOpenSubId(null);
      if (drillRef.current?.id === subId) {
        // O painel principal está com `display: none`; a linha só volta a
        // existir para o foco depois do próximo commit.
        rowToRefocus.current = subId;
        setDrill(null);
        return;
      }
      const row = panelEl?.querySelector<HTMLElement>(`[data-menu-sub="${subId}"]`);
      if (row) focusItem(row);
    },
    [panelEl],
  );

  const leaveSubs = useCallback((): void => {
    // Um submenu em drill-down NÃO fecha por hover: ele substituiu o painel, e o
    // ponteiro está necessariamente dentro dele.
    if (drillRef.current) return;
    setOpenSubId(null);
  }, []);

  // Posiciona e acompanha: `fixed` foge do overflow dos ancestrais, mas paga
  // com o reposicionamento em toda rolagem (captura, para pegar contêineres
  // internos) e em todo resize.
  useLayoutEffect(() => {
    if (!open || !panelEl) return;
    const place = (): void => {
      const t = triggerRef.current;
      if (!t) return;
      const next = placePanel(t, panelEl, readingRect(t), placement, align);
      setDirection(next.direction);
      setCoords({ top: next.top, left: next.left });
    };
    place();
    // O conteúdo pode crescer DEPOIS da abertura (o catálogo de perfis chega por
    // query). Sem observar essa altura, o painel fica ancorado na medida inicial
    // e suas últimas linhas nascem fora da viewport.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(place);
    observer?.observe(panelEl);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      observer?.disconnect();
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, panelEl, placement, align]);

  // Primeiro foco: o item marcado quando há um (a lista de rádio abre onde a
  // pessoa parou), a primeira linha caso contrário.
  useLayoutEffect(() => {
    if (!open || !panelEl || !coords || focusedOnOpen.current) return;
    const pane = panelEl.querySelector<HTMLElement>('[data-menu-pane="root"]');
    if (!pane) return;
    const items = itemsOf(pane);
    if (items.length === 0) return;
    focusedOnOpen.current = true;
    const target =
      openEdge.current === 'last'
        ? items[items.length - 1]
        : (items.find((el) => el.getAttribute('aria-checked') === 'true') ?? items[0]);
    if (target) focusItem(target);
  }, [open, panelEl, coords]);

  // Entrou no drill-down: foco na primeira opção, não no "Voltar".
  useLayoutEffect(() => {
    if (!drill || !drillHost) return;
    const pane = drillHost.closest<HTMLElement>('[data-menu-pane="drill"]');
    if (!pane) return;
    const items = itemsOf(pane);
    const target = items.find((el) => !el.hasAttribute('data-menu-back')) ?? items[0];
    if (target) focusItem(target);
  }, [drill, drillHost]);

  // Saiu do drill-down: foco de volta na linha que o abriu.
  useLayoutEffect(() => {
    const subId = rowToRefocus.current;
    if (!subId || drill || !panelEl) return;
    rowToRefocus.current = null;
    const row = panelEl.querySelector<HTMLElement>(`[data-menu-sub="${subId}"]`);
    if (row) focusItem(row);
  }, [drill, panelEl]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent): void => {
      const target = event.target as Element | null;
      // Gatilho e painéis (inclusive os submenus, que são portais irmãos)
      // carregam o mesmo dono: um clique neles não é um clique fora.
      if (target?.closest?.(`[data-menu-owner="${ownerId}"]`)) return;
      close(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, ownerId, close]);

  /**
   * Um só handler, no painel raiz. Os submenus são portais, e no React os
   * eventos de um portal sobem pela árvore de componentes, não pelo DOM: eles
   * chegam aqui. Qual painel está ativo sai de onde o foco está.
   */
  const onKeyDown = (event: ReactKeyboardEvent): void => {
    const active = document.activeElement as HTMLElement | null;
    // Um clique num ponto morto (divisor, padding) deixa o foco no PRÓPRIO
    // painel: as teclas valem para o painel que está à vista.
    const pane =
      active?.closest<HTMLElement>('[data-menu-pane]') ??
      (active && active === panelEl
        ? panelEl.querySelector<HTMLElement>('[data-menu-pane]:not([hidden])')
        : null);
    if (!pane) return;
    const items = itemsOf(pane);
    const index = active ? items.indexOf(active) : -1;
    const paneSubId = pane.dataset.menuPane === 'root' ? null : (pane.dataset.menuSub ?? null);

    // Navegar abandona a busca por prefixo: depois de um Home ou de uma seta, a
    // próxima letra começa uma busca nova em vez de continuar a anterior.
    if (event.key.length > 1 && !MODIFIER_KEYS.has(event.key)) typeahead.current.query = '';

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        if (paneSubId) closeSub(paneSubId);
        else close();
        break;
      case 'Tab':
        // Sem `preventDefault`: o foco volta ao gatilho e o navegador segue a
        // tabulação a partir dele, que é onde a pessoa espera continuar.
        close();
        break;
      case 'ArrowDown':
        event.preventDefault();
        moveFocus(items, index, 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveFocus(items, index, -1);
        break;
      case 'Home':
        event.preventDefault();
        if (items[0]) focusItem(items[0]);
        break;
      case 'End':
        event.preventDefault();
        if (items.length > 0) focusItem(items[items.length - 1]!);
        break;
      case 'ArrowLeft':
        if (!paneSubId) break;
        event.preventDefault();
        closeSub(paneSubId);
        break;
      default: {
        if (event.key.length !== 1) break;
        if (event.ctrlKey || event.metaKey || event.altKey) break;
        const now = Date.now();
        const state = typeahead.current;
        const searching = state.query !== '' && now - state.at <= TYPEAHEAD_RESET_MS;
        // Fora de uma busca o Espaço ATIVA a linha focada, o atalho mais óbvio
        // de um `<button>`. Durante uma, ele é parte do nome ("professora de
        // l"): ativar ali escolheria a primeira linha que casou com o prefixo.
        if (event.key === ' ' && !searching) break;
        event.preventDefault();
        state.query = searching ? state.query + event.key : event.key;
        state.at = now;
        const query = normalize(state.query);
        const from = index < 0 ? 0 : index;
        // Uma letra salta para a PRÓXIMA linha que começa com ela; uma busca já
        // em curso inclui a linha atual, que costuma ser a que está crescendo.
        const offset = state.query.length > 1 ? from : from + 1;
        const ordered = [...items.slice(offset), ...items.slice(0, offset)];
        const hit = ordered.find((el) => itemText(el).startsWith(query));
        if (hit) focusItem(hit);
        break;
      }
    }
  };

  const childRef = (trigger as unknown as { ref?: Ref<HTMLElement> }).ref;
  const triggerProps = trigger.props as {
    onClick?: (event: React.MouseEvent) => void;
    onKeyDown?: (event: ReactKeyboardEvent) => void;
  };

  const clonedTrigger = cloneElement(trigger, {
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node;
      if (typeof childRef === 'function') childRef(node);
      else if (childRef && 'current' in childRef) {
        (childRef as React.MutableRefObject<HTMLElement | null>).current = node;
      }
    },
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'data-menu-owner': ownerId,
    onClick: (event: React.MouseEvent) => {
      triggerProps.onClick?.(event);
      if (event.defaultPrevented) return;
      if (open) close();
      else openMenu('first');
    },
    onKeyDown: (event: ReactKeyboardEvent) => {
      triggerProps.onKeyDown?.(event);
      if (event.defaultPrevented || open) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        openMenu('first');
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        openMenu('last');
      }
    },
  } as Record<string, unknown>);

  const context: MenuContextValue = {
    ownerId,
    close: () => close(),
    openSubId,
    drillId: drill?.id ?? null,
    drillHost,
    requestSub,
    closeSub,
    leaveSubs,
    areaRect,
  };

  return (
    <MenuContext.Provider value={context}>
      {clonedTrigger}
      {open && (
        <Portal>
          <div
            ref={setPanelEl}
            className={cn(
              'menu__panel',
              `menu__panel--${direction}`,
              reduceMotion && 'menu__panel--instant',
              className,
            )}
            style={{
              top: coords?.top ?? 0,
              left: coords?.left ?? 0,
              // Um quadro invisível antes de medir: sem isto o painel pisca no
              // canto superior esquerdo antes de achar o gatilho.
              visibility: coords ? undefined : 'hidden',
            }}
            role="menu"
            aria-label={drill?.label ?? label}
            data-menu-owner={ownerId}
            // Alvo de foco, não parada de tabulação: um clique fora dos itens
            // pousa o foco aqui, onde o `onKeyDown` ouve, em vez do <body>.
            tabIndex={-1}
            onKeyDown={onKeyDown}
          >
            <div className="menu__pane" data-menu-pane="root" hidden={drill !== null}>
              {children}
            </div>
            {drill && (
              <div className="menu__pane" data-menu-pane="drill" data-menu-sub={drill.id}>
                <button
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  data-menu-item
                  data-menu-back
                  className="menu__item menu__back"
                  onClick={() => closeSub(drill.id)}
                >
                  <span className="menu__item-leading" aria-hidden="true">
                    <ChevronLeft size={16} />
                  </span>
                  <span className="menu__item-label">Voltar</span>
                </button>
                <div ref={setDrillHost} />
              </div>
            )}
          </div>
        </Portal>
      )}
    </MenuContext.Provider>
  );
}

function moveFocus(items: HTMLElement[], index: number, delta: number): void {
  if (items.length === 0) return;
  const next =
    index < 0 ? (delta > 0 ? 0 : items.length - 1) : (index + delta + items.length) % items.length;
  focusItem(items[next]!);
}

export interface MenuGroupProps {
  label: string;
  children: ReactNode;
}

/**
 * Bloco nomeado. O cabeçalho é pintura: `aria-hidden` nele e `aria-label` no
 * grupo dizem a mesma coisa uma vez só, e as setas nunca param nele.
 */
export function MenuGroup({ label, children }: MenuGroupProps): JSX.Element {
  return (
    <div className="menu__group" role="group" aria-label={label}>
      <p className="menu__group-label" aria-hidden="true">
        {label}
      </p>
      {children}
    </div>
  );
}

export function MenuDivider(): JSX.Element {
  return <div className="menu__divider" role="separator" />;
}

export interface MenuScrollAreaProps {
  /** Teto em px. O padrão mostra cerca de oito linhas. */
  maxHeight?: number;
  /** Nome do bloco que rola, para quem navega por leitor de tela. */
  label?: string;
  className?: string;
  children: ReactNode;
}

/**
 * Bloco que rola sozinho, para a lista que pode crescer (agentes, áreas) sem
 * empurrar para fora do painel as linhas fixas que vêm depois dela.
 */
export function MenuScrollArea({
  maxHeight = 280,
  label,
  className,
  children,
}: MenuScrollAreaProps): JSX.Element {
  return (
    <div
      className={cn('menu__scroll', className)}
      style={{ maxHeight }}
      /* `group`, e não um `div` cru: dentro de `role="menu"` só entram papéis de
         menu, e um contêiner anônimo mas FOCÁVEL vira um filho ilegal. Como
         grupo, ele é um bloco legítimo do menu (o mesmo papel que `MenuGroup`
         usa) e pode ficar na ordem de tabulação.

         O `tabIndex` existe porque o roving tabindex é POR PAINEL: quando o
         único `tabindex=0` está numa linha FIXA (uma `MenuSub` focada), esta
         região rolável fica sem nenhum filho tabulável e passa a ser uma área de
         rolagem inalcançável por teclado. O zero a devolve à ordem sem mudar
         nada da navegação por setas, que lê `[data-menu-item]` do DOM; e como
         `Tab` FECHA o menu, a parada extra dura o tempo de uma tecla. */
      role="group"
      aria-label={label}
      tabIndex={0}
    >
      {children}
    </div>
  );
}

interface RowBaseProps {
  disabled?: boolean;
  leading?: ReactNode;
  /** Segunda linha em tom terciário: papel da persona, explicação curta. */
  description?: ReactNode;
  /** Slot à direita: valor atual, atalho, spinner de gravação. */
  trailing?: ReactNode;
  children: ReactNode;
}

export interface MenuItemProps extends RowBaseProps {
  onSelect: () => void;
  /** Ação destrutiva: vermelho no texto e no ícone, fundo no hover e no foco. */
  danger?: boolean;
}

/**
 * Uma ação. Selecionar FECHA o menu (incluindo submenus) e devolve o foco ao
 * gatilho — quem escolheu já terminou com o painel.
 */
export function MenuItem({
  onSelect,
  danger,
  disabled,
  leading,
  description,
  trailing,
  children,
}: MenuItemProps): JSX.Element {
  const ctx = useMenuContext('MenuItem');
  return (
    <button
      type="button"
      role="menuitem"
      tabIndex={-1}
      data-menu-item
      aria-disabled={disabled || undefined}
      className={cn('menu__item', danger && 'menu__item--danger')}
      onClick={() => {
        if (disabled) return;
        ctx.close();
        onSelect();
      }}
      onPointerEnter={(event) => hoverFocus(event, ctx)}
    >
      <RowBody leading={leading} description={description} trailing={trailing}>
        {children}
      </RowBody>
    </button>
  );
}

export interface MenuRadioItemProps extends RowBaseProps {
  checked: boolean;
  onSelect: () => void;
}

/** Escolha única dentro de um grupo: `menuitemradio` e um `Check` no marcado. */
export function MenuRadioItem({
  checked,
  onSelect,
  disabled,
  leading,
  description,
  trailing,
  children,
}: MenuRadioItemProps): JSX.Element {
  const ctx = useMenuContext('MenuRadioItem');
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={checked}
      tabIndex={-1}
      data-menu-item
      aria-disabled={disabled || undefined}
      className={cn('menu__item', checked && 'menu__item--checked')}
      onClick={() => {
        if (disabled) return;
        ctx.close();
        onSelect();
      }}
      onPointerEnter={(event) => hoverFocus(event, ctx)}
    >
      <RowBody leading={leading} description={description} trailing={trailing}>
        {children}
      </RowBody>
      <span className="menu__item-check" aria-hidden="true">
        {checked ? <Check size={16} /> : null}
      </span>
    </button>
  );
}

export interface MenuSubProps {
  /** Rótulo da linha e nome acessível do submenu. */
  label: string;
  /** Valor atual, à direita da linha. */
  value?: ReactNode;
  disabled?: boolean;
  leading?: ReactNode;
  /** Um `<MenuSubContent>` com as linhas do submenu. */
  children: ReactNode;
}

/**
 * Linha que abre outro painel. Abre por clique, `Enter`, `ArrowRight` e por
 * hover com intenção; fecha por `ArrowLeft` e `Escape`, devolvendo o foco à
 * própria linha. Hover NÃO move o foco: o mouse abre para olhar, o teclado abre
 * para escolher.
 */
export function MenuSub({ label, value, disabled, leading, children }: MenuSubProps): JSX.Element {
  const ctx = useMenuContext('MenuSub');
  const subId = useId().replace(/:/g, '');
  const rowRef = useRef<HTMLButtonElement | null>(null);
  const [panel, setPanel] = useState<HTMLDivElement | null>(null);
  const [coords, setCoords] = useState<Coords | null>(null);
  const hoverTimer = useRef<number | undefined>(undefined);
  const focusedOnOpen = useRef(false);
  const shouldFocus = useRef(true);
  const reduceMotion = usePrefersReducedMotion();

  const open = ctx.openSubId === subId;
  const drilled = ctx.drillId === subId;
  const side = open && !drilled;

  const openSub = (byHover: boolean): void => {
    const row = rowRef.current;
    if (disabled || !row) return;
    // Um clique ou uma tecla cancelam a intenção de hover que estava correndo:
    // sem isto ela reabriria o submenu 150ms depois, por cima da escolha.
    window.clearTimeout(hoverTimer.current);
    shouldFocus.current = !byHover;
    focusedOnOpen.current = false;
    setCoords(null);
    ctx.requestSub(subId, label, subMode(row, ctx.areaRect()));
  };

  useEffect(() => {
    if (!open) focusedOnOpen.current = false;
  }, [open]);

  useEffect(() => () => window.clearTimeout(hoverTimer.current), []);

  useLayoutEffect(() => {
    if (!side || !panel) return;
    const place = (): void => {
      const row = rowRef.current;
      if (!row) return;
      setCoords(placeSubPanel(row, panel, ctx.areaRect()));
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
    // `ctx.areaRect` é estável (useCallback no Menu).
  }, [side, panel, ctx.areaRect]);

  useLayoutEffect(() => {
    if (!side || !panel || !coords || focusedOnOpen.current) return;
    focusedOnOpen.current = true;
    if (!shouldFocus.current) return;
    const first = itemsOf(panel)[0];
    if (first) focusItem(first);
  }, [side, panel, coords]);

  return (
    <>
      <button
        ref={rowRef}
        type="button"
        role="menuitem"
        tabIndex={-1}
        data-menu-item
        data-menu-sub={subId}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-disabled={disabled || undefined}
        className={cn('menu__item', open && 'menu__item--open')}
        onClick={() => (open ? ctx.closeSub(subId) : openSub(false))}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowRight') return;
          event.preventDefault();
          event.stopPropagation();
          if (open) {
            const first = panel ? itemsOf(panel)[0] : undefined;
            if (first) focusItem(first);
          } else {
            openSub(false);
          }
        }}
        onPointerEnter={(event) => {
          if (event.pointerType === 'touch' || disabled) return;
          if (ctx.openSubId !== subId) ctx.leaveSubs();
          focusItem(event.currentTarget);
          if (open) return;
          window.clearTimeout(hoverTimer.current);
          hoverTimer.current = window.setTimeout(() => openSub(true), HOVER_INTENT_MS);
        }}
        onPointerLeave={() => window.clearTimeout(hoverTimer.current)}
      >
        <RowBody leading={leading} description={undefined} trailing={value}>
          {label}
        </RowBody>
        <span className="menu__item-chevron" aria-hidden="true">
          <ChevronRight size={16} />
        </span>
      </button>

      {side && (
        <Portal>
          <div
            ref={setPanel}
            className={cn(
              'menu__panel',
              'menu__panel--sub',
              reduceMotion && 'menu__panel--instant',
            )}
            style={{
              top: coords?.top ?? 0,
              left: coords?.left ?? 0,
              visibility: coords ? undefined : 'hidden',
            }}
            role="menu"
            aria-label={label}
            data-menu-owner={ctx.ownerId}
            data-menu-pane="sub"
            data-menu-sub={subId}
            tabIndex={-1}
            onPointerEnter={() => window.clearTimeout(hoverTimer.current)}
          >
            {children}
          </div>
        </Portal>
      )}
      {drilled && ctx.drillHost && createPortal(children, ctx.drillHost)}
    </>
  );
}

/** As linhas de um submenu. Onde elas aparecem é decisão do `MenuSub`. */
export function MenuSubContent({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return <div className={cn('menu__sub-content', className)}>{children}</div>;
}

function RowBody({ leading, description, trailing, children }: RowBaseProps): JSX.Element {
  return (
    <>
      {leading && (
        <span className="menu__item-leading" aria-hidden="true">
          {leading}
        </span>
      )}
      <span className="menu__item-body">
        <span className="menu__item-label">{children}</span>
        {description && <span className="menu__item-description">{description}</span>}
      </span>
      {trailing !== undefined && trailing !== null && trailing !== false && (
        <span className="menu__item-trailing">{trailing}</span>
      )}
    </>
  );
}

/**
 * O mouse passando por uma linha a torna a linha ativa: uma só marca de "aqui"
 * para mouse e teclado. De quebra, fecha o submenu que o hover tinha aberto na
 * linha anterior.
 */
function hoverFocus(event: ReactPointerEvent<HTMLElement>, ctx: MenuContextValue): void {
  if (event.pointerType === 'touch') return;
  const el = event.currentTarget;
  if (el.getAttribute('aria-disabled') === 'true') return;
  // Só uma linha do painel PRINCIPAL fecha o submenu ao ser sobrevoada: uma
  // linha dentro do próprio submenu fecharia o painel debaixo do ponteiro.
  if (el.closest<HTMLElement>('[data-menu-pane]')?.dataset.menuPane === 'root') ctx.leaveSubs();
  focusItem(el);
}
